import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { House, GlobeHemisphereWest, User } from 'phosphor-react';

// changed by Cursor: language options for mobile bottom sheet
const languageOptions = [
  { name: 'German', path: '/learner/language-learning', flag: '/Flag/De.png' },
  { name: 'English', path: '/learner/english-language-learning', flag: '/Flag/En.svg' },
  { name: 'Japanese', path: '/learner/japanese-language-learning', flag: '/Flag/Ja.png' },
];

// Detect if running in webview
const isWebView = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Check localStorage flag first (can be set by native app)
  // This is the most reliable way for native apps to signal webview
  if (localStorage.getItem('isWebView') === 'true') {
    console.log('🔍 WebView detected: localStorage flag');
    return true;
  }

  // Check URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('webview') === 'true') {
    console.log('🔍 WebView detected: URL parameter');
    return true;
  }

  // React Native WebView detection (most reliable)
  if (window.ReactNativeWebView) {
    console.log('🔍 WebView detected: ReactNativeWebView');
    return true;
  }

  // Check for custom webview identifier
  if (window.__WEBVIEW__ || window.isWebView) {
    console.log('🔍 WebView detected: custom identifier');
    return true;
  }

  // WebKit message handlers (iOS) - only if explicitly set
  if (window.webkit && window.webkit.messageHandlers && Object.keys(window.webkit.messageHandlers).length > 0) {
    console.log('🔍 WebView detected: WebKit messageHandlers');
    return true;
  }

  const userAgent = navigator.userAgent || '';
  const userAgentLower = userAgent.toLowerCase();

  // Android WebView detection - be more specific
  // Android webview has "wv" in user agent AND doesn't have Chrome
  if (userAgentLower.includes('wv') && !userAgentLower.includes('chrome')) {
    console.log('🔍 WebView detected: Android wv pattern');
    return true;
  }

  // Don't detect based on iOS patterns in browser dev tools
  // Only rely on explicit flags or React Native WebView
  // This prevents false positives in Chrome DevTools mobile emulation

  return false;
};

const Navbar = () => {
  const [isInWebView, setIsInWebView] = useState(false);
  const [languageSheetOpen, setLanguageSheetOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // changed by Cursor: mobile bottom bar = Home, Courses, Profile (with labels)
  const mobileNavItems = [
    { name: 'Home', path: '/learner/dashboard', icon: House },
    { name: 'Courses', path: null, icon: GlobeHemisphereWest, isLanguageTrigger: true },
    { name: 'Profile', path: '/learner/profile', icon: User },
  ];

  useEffect(() => {
    // Detect webview
    const webViewDetected = isWebView();
    setIsInWebView(webViewDetected);
    
    // Debug: Check viewport width
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth <= 768;
    
    console.log('📱 Navbar - isWebView:', webViewDetected, 'UserAgent:', navigator.userAgent);
    console.log('📱 Navbar - Viewport:', viewportWidth, 'isMobile:', isMobile, 'shouldShow:', !webViewDetected && isMobile);
  }, []);


  // Check if mobile viewport
  const [isMobile, setIsMobile] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Hide navbar when virtual keyboard is open on mobile
  useEffect(() => {
    if (!window.visualViewport) return;
    const vv = window.visualViewport;
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - vv.height;
      setKeyboardOpen(heightDiff > 150);
    };
    vv.addEventListener('resize', handleViewportResize);
    return () => vv.removeEventListener('resize', handleViewportResize);
  }, []);

  // Debug: Log render state
  const shouldShow = !isInWebView && isMobile;
  console.log('📱 Navbar RENDER - isInWebView:', isInWebView, 'isMobile:', isMobile, 'shouldShow:', shouldShow);

  const handleLanguageSelect = (path) => {
    setLanguageSheetOpen(false);
    navigate(path);
  };

  // Don't render at all if webview
  if (isInWebView) {
    return null;
  }

  const navItemBase =
    'flex flex-col items-center justify-center gap-1 flex-1 py-2 px-1 bg-transparent border-none text-[#999] dark:text-[var(--text-tertiary)] no-underline transition-all duration-200 cursor-pointer min-w-[44px]';
  const navItemActive = 'text-[#12766d] dark:text-[var(--accent-teal)]';
  const navItemHover = 'hover:text-[#666] dark:hover:text-[var(--text-secondary)]';

  return (
    <>
      {/* Bottom Navigation Bar - mobile: Home, Courses, Profile (with labels) */}
      <nav
        className={`navbar-bottom fixed bottom-0 left-0 right-0 top-auto z-[10005] h-[72px] bg-white dark:bg-[var(--navbar-bg)] border-t border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)] shadow-[0_-2px_8px_rgba(0,0,0,0.06)] dark:shadow-[var(--navbar-shadow)] flex flex-row items-center justify-around pt-1.5 px-2 pb-[max(6px,env(safe-area-inset-bottom))] hidden max-md:flex opacity-[0.98] ${isInWebView ? 'webview-hidden' : ''}`}
        style={isMobile ? { display: keyboardOpen ? 'none' : 'flex' } : undefined}
      >
        {isMobile
          ? mobileNavItems.map((item) => {
              const IconComponent = item.icon;
              if (item.isLanguageTrigger) {
                return (
                  <button
                    key="language"
                    type="button"
                    className={`${navItemBase} ${navItemHover} ${languageSheetOpen ? navItemActive : ''}`}
                    onClick={() => setLanguageSheetOpen(true)}
                    aria-label="Courses"
                  >
                    <IconComponent size={24} weight={languageSheetOpen ? 'fill' : 'regular'} />
                    <span className="text-[11px] font-medium leading-tight">{item.name}</span>
                  </button>
                );
              }
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `${navItemBase} ${navItemHover} ${isActive ? navItemActive : ''}`}
                  aria-label={item.name}
                >
                  <IconComponent size={24} weight={location.pathname === item.path ? 'fill' : 'regular'} />
                  <span className="text-[11px] font-medium leading-tight">{item.name}</span>
                </NavLink>
              );
            })
          : null}
      </nav>

      {/* Language bottom sheet - overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-[10006] transition-opacity duration-300 ${
          languageSheetOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setLanguageSheetOpen(false)}
        onKeyDown={(e) => e.key === 'Escape' && setLanguageSheetOpen(false)}
        role="button"
        tabIndex={0}
        aria-label="Close language menu"
      />

      {/* Language bottom sheet */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white dark:bg-[var(--navbar-bg)] z-[10007] rounded-t-[20px] shadow-[0_-4px_16px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_16px_rgba(0,0,0,0.3)] p-4 pb-[max(24px,env(safe-area-inset-bottom))] transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          languageSheetOpen ? 'translate-y-0' : 'translate-y-full pointer-events-none'
        }`}
        role="dialog"
        aria-label="Choose language"
      >
        <div className="w-10 h-1 bg-[rgba(26,26,46,0.2)] dark:bg-white/20 rounded-sm mx-auto mb-4" aria-hidden />
        <p className="text-base font-semibold text-[rgba(26,26,46,0.9)] dark:text-[var(--text-primary)] m-0 mb-4">
          Choose language
        </p>
        <div className="flex flex-col gap-2">
          {languageOptions.map((opt) => (
            <button
              key={opt.path}
              type="button"
              className="flex items-center gap-3 w-full py-3.5 px-4 border-none rounded-[10px] bg-transparent text-[rgba(26,26,46,0.8)] dark:text-[var(--text-secondary)] text-[15px] font-medium text-left cursor-pointer transition-all duration-200 hover:bg-[rgba(18,118,109,0.08)] hover:text-[#1d4746] dark:hover:bg-[var(--accent-teal-light)] dark:hover:text-[var(--text-primary)] active:scale-[0.98]"
              onClick={() => handleLanguageSelect(opt.path)}
              aria-label={opt.name}
            >
              <img src={opt.flag} alt="" width={28} height={21} className="object-contain" />
              <span>{opt.name}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default Navbar;

